import { Gift, PartyPopper, Sparkles } from 'lucide-react';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { formatDateTime } from '../../../../lib/format.js';
import type { SpinOutcome, SpinStatus } from '../types/rewards.js';
import { useCountdownLabel } from '../lib/use-countdown-label.js';
import { CopyCodeButton } from './copy-code-button.js';
import { formatDiscountSummary } from './discount-summary.js';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof StorefrontApiError) return error.message;
  return fallback;
}

interface SpinResultPanelProps {
  outcome: SpinOutcome;
  onDismiss: () => void;
}

/** Renders exactly what `POST /rewards/spin` returned -- never a
 * client-guessed outcome. See milestone brief: "no client-side
 * reward-probability simulation". */
function SpinResultPanel({ outcome, onDismiss }: SpinResultPanelProps) {
  if (outcome.prizeType === 'no_win') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-6 py-8 text-center">
        <Sparkles className="h-8 w-8 text-gray-400" aria-hidden="true" />
        <p className="text-base font-semibold text-gray-900">{outcome.label || 'So close! Not this time.'}</p>
        <p className="max-w-sm text-sm text-gray-600">
          Thanks for spinning -- come back for your next chance to win.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="focus-visible:ring-brand rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
        >
          Got it
        </button>
      </div>
    );
  }

  if (outcome.prizeType === 'points') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-brand bg-brand/10 px-6 py-8 text-center">
        <PartyPopper className="h-8 w-8 text-gray-900" aria-hidden="true" />
        <p className="text-base font-semibold text-gray-900">{outcome.label}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="focus-visible:ring-brand rounded-full bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2"
        >
          Nice!
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-brand bg-brand/10 px-6 py-8 text-center">
      <Gift className="h-8 w-8 text-gray-900" aria-hidden="true" />
      <p className="text-base font-semibold text-gray-900">{outcome.label}</p>
      <p className="text-sm text-gray-700">{formatDiscountSummary(outcome)}</p>
      <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2">
        <code className="text-sm font-bold tracking-wide text-gray-900">{outcome.couponCode}</code>
        <CopyCodeButton value={outcome.couponCode} />
      </div>
      <p className="text-xs text-gray-500">Paste this code at checkout to apply your discount.</p>
      <button
        type="button"
        onClick={onDismiss}
        className="focus-visible:ring-brand rounded-full bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2"
      >
        Done
      </button>
    </div>
  );
}

interface SpinWheelCardProps {
  status: SpinStatus | undefined;
  isStatusLoading: boolean;
  isStatusError: boolean;
  onRetryStatus: () => void;
  outcome: SpinOutcome | null;
  onSpin: () => void;
  onDismissResult: () => void;
  isSpinning: boolean;
  spinError: string | null;
}

export function SpinWheelCard({
  status,
  isStatusLoading,
  isStatusError,
  onRetryStatus,
  outcome,
  onSpin,
  onDismissResult,
  isSpinning,
  spinError,
}: SpinWheelCardProps) {
  const countdownLabel = useCountdownLabel(status?.canSpin === false ? status.nextEligibleAt : null);

  if (isStatusLoading) {
    return (
      <div className="animate-pulse rounded-2xl border border-gray-100 bg-white px-6 py-12 text-center text-sm text-gray-400">
        Checking your spin eligibility…
      </div>
    );
  }

  if (isStatusError) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 rounded-2xl border border-red-100 bg-red-50 px-6 py-10 text-center">
        <p className="text-sm font-medium text-red-700">Could not load the spin wheel right now.</p>
        <button
          type="button"
          onClick={onRetryStatus}
          className="focus-visible:ring-brand rounded-full border border-red-200 bg-white px-4 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!status || status.rewardConfigId === null) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
        <Sparkles className="h-8 w-8 text-gray-300" aria-hidden="true" />
        <p className="text-base font-semibold text-gray-900">Spin wheel isn&apos;t available right now</p>
        <p className="max-w-sm text-sm text-gray-500">Check back later for a chance to win rewards.</p>
      </div>
    );
  }

  if (outcome) {
    return <SpinResultPanel outcome={outcome} onDismiss={onDismissResult} />;
  }

  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-100 bg-white px-6 py-10 text-center">
      <div
        className={`flex h-32 w-32 items-center justify-center rounded-full border-4 border-brand bg-brand/10 ${isSpinning ? 'animate-spin' : ''}`}
        aria-hidden="true"
      >
        <Gift className="h-12 w-12 text-gray-900" />
      </div>

      {status.canSpin ? (
        <>
          <p className="text-sm text-gray-600">You have a spin available. Good luck!</p>
          <button
            type="button"
            onClick={onSpin}
            disabled={isSpinning}
            className="focus-visible:ring-brand rounded-full bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSpinning ? 'Spinning…' : 'Spin now'}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-700">You&apos;ve already used your spin.</p>
          <p className="text-sm text-gray-500">
            {countdownLabel
              ? `Next spin ${countdownLabel}`
              : status.nextEligibleAt
                ? `Next spin available ${formatDateTime(status.nextEligibleAt)}`
                : 'Check back soon for your next spin.'}
          </p>
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-full bg-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-500"
          >
            Spin now
          </button>
        </>
      )}

      {spinError && <p className="text-sm font-medium text-red-600">{spinError}</p>}
    </div>
  );
}

export { errorMessage };
